import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectOffice } from '~/actions';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Autocomplete, Column, ConfirmPopup, Row } from '~/commonComponents';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';

interface IProps {
  description: string;
  targetUrl: string;
  text: string;
  isDisabled?: boolean;
  onTrackEvent?: () => void;
  /** DS Button color. Use `inverse` for the white pill on a colored card. @default 'primary' */
  color?: 'primary' | 'secondary' | 'error' | 'cta' | 'inverse';
  style?: React.CSSProperties;
  textStyle?: React.CSSProperties;
}

export const OfficeAuthorizedButton = ({
  description,
  targetUrl,
  text,
  isDisabled = false,
  onTrackEvent,
  color = 'primary',
  style,
  textStyle,
}: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const offices = useAppSelector((state) => state.user.offices);

  const [isLoading, setLoading] = useState(false);
  const [isShowPopup, setShowPopup] = useState(false);
  const [value, setValue] = useState(currentOffice?.id);

  const isUseDSOStructure = useRef(shouldUseDsoStructure()).current;

  const handleSelectOffice = () => {
    if (!value) {
      return;
    }

    setLoading(true);
    dispatch(selectOffice({ officeId: value, redirectUrl: targetUrl }));
  };

  const handleClick = () => {
    if (isUseDSOStructure && !currentOffice) {
      setShowPopup(true);
      return;
    }

    navigate(targetUrl);
    onTrackEvent?.();
  };

  const handleClosePopup = () => {
    setValue(currentOffice?.id);
    setShowPopup(false);
  };

  useEffect(() => {
    setValue(currentOffice?.id);
  }, [currentOffice?.id]);

  return (
    <>
      <Button
        data-cy={text === 'Post A Shift' ? 'post-shift-button' : undefined}
        disabled={isDisabled}
        // width 206 has no DS size token; kept as inline style for the dashboard top-button row
        style={{ width: 206, ...style }}
        variant="contained"
        color={color}
        size="medium"
        onClick={handleClick}
      >
        <span style={textStyle}>{text}</span>
      </Button>
      {isShowPopup && (
        <ConfirmPopup
          title="Select Office"
          rightButtonAction={handleSelectOffice}
          leftButtonAction={handleClosePopup}
          rightButtonText="Select Office"
          leftButtonText="Go Back"
          titleStyle={{
            fontSize: 18,
          }}
          leftButtonDisabled={isLoading}
          rightButtonDisabled={isLoading || !value}
          rightButtonLoading={isLoading}
        >
          <Column alignItems="center">
            <Typography
              as="span"
              textAlign="center"
              color={colors.neutral[500]}
              style={{ width: '85%' }}
            >
              {description}
            </Typography>
            <Row
              marginTop={20}
              marginBottom={48}
              alignItems="center"
              justifyContent="center"
              gap={16}
            >
              <Typography as="span" bold>
                Office Name:
              </Typography>
              <Autocomplete
                options={offices.map((it) => ({
                  label: it.office_name,
                  value: it.id,
                }))}
                value={value}
                onChange={({ value }) => {
                  setValue(value);
                }}
              />
            </Row>
          </Column>
        </ConfirmPopup>
      )}
    </>
  );
};
